import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Phone, MapPin, Star, Package, DollarSign } from 'lucide-react';
import axios from 'axios';
import ProductReviews from '../components/ProductReviews';
import { PaymentMethodBadge } from '../components/PaymentIcons';

const ProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [loading, setLoading] = useState(true);
  const { API, siteSettings, user } = useContext(AuthContext);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      // Get all products and find the specific one (since we don't have a single product endpoint)
      const response = await axios.get(`${API}/products`);
      const foundProduct = response.data.find(p => p.id === productId);
      
      if (foundProduct) {
        setProduct(foundProduct);
        
        // Fetch producer's payment methods
        try {
          const paymentResponse = await axios.get(`${API}/producers/${foundProduct.producer_id}/payment-methods`);
          setPaymentMethods(paymentResponse.data);
        } catch (paymentError) {
          console.error('Error fetching payment methods:', paymentError);
          // Set default if not found
          setPaymentMethods({
            pix: false,
            debit_card: false,
            credit_card: false,
            credit_card_brands: []
          });
        }
      } else {
        toast.error('Produto não encontrado');
        navigate('/marketplace');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Erro ao carregar produto');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const canAccessWhatsApp = () => {
    console.log('canAccessWhatsApp check:', {
      whatsapp_requires_login: siteSettings?.whatsapp_requires_login,
      user: user,
      hasUser: !!user
    });
    
    // Se a configuração exige login, verifica se o usuário está logado
    if (siteSettings?.whatsapp_requires_login) {
      return !!user;
    }
    // Se não exige login, sempre permite acesso
    return true;
  };

  const handleWhatsAppClick = () => {
    if (!canAccessWhatsApp()) {
      toast.error('Você precisa estar logado para contatar o produtor');
      navigate('/login');
      return;
    }
    
    openWhatsApp(product.producer_phone, product.name, product.producer_name, product.id);
  };

  const openWhatsApp = async (phone, productName, producerName, productId) => {
    try {
      // Track the click
      await axios.post(`${API}/products/${productId}/click`);
    } catch (error) {
      console.error('Error tracking click:', error);
      // Continue with WhatsApp even if tracking fails
    }

    const message = encodeURIComponent(
      `Olá ${producerName}! Vi seu produto "${productName}" na ${siteSettings?.site_name || 'Nossa Feira'} e tenho interesse. Podemos conversar?`
    );
    const cleanPhone = phone.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/55${cleanPhone}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleReviewAdded = () => {
    // Refresh product data to get updated ratings
    fetchProduct();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="h-80 bg-gray-200 rounded-lg"></div>
              <div className="space-y-4">
                <div className="h-8 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-16 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back Button */}
        <Button
          onClick={() => navigate('/marketplace')}
          variant="outline"
          className="mb-6 flex items-center gap-2"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar ao Marketplace
        </Button>

        {/* Product Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Product Image */}
          <Card className="card-feira overflow-hidden">
            <div className="aspect-square relative">
              {product.image_url ? (
                <>
                  <img
                    src={`${product.image_url}`}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-gradient-to-br from-green-100 to-orange-100 flex items-center justify-center" style={{display: 'none'}}>
                    <Package className="w-24 h-24 text-green-500" />
                  </div>
                </>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-green-100 to-orange-100 flex items-center justify-center">
                  <Package className="w-24 h-24 text-green-500" />
                </div>
              )}
              <Badge className="absolute top-4 left-4 category-badge">
                {product.category}
              </Badge>
            </div>
          </Card>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2">{product.name}</h1>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-1">
                  <Star className="w-5 h-5 text-yellow-400 fill-current" />
                  <span className="font-medium">
                    {product.average_rating ? product.average_rating.toFixed(1) : '0.0'}
                  </span>
                  <span className="text-gray-500">
                    ({product.total_reviews || 0} avaliação{product.total_reviews !== 1 ? 'ões' : ''})
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-6 h-6 text-green-600" />
                  <span className="text-lg font-medium">Preço:</span>
                </div>
                <span className="text-3xl font-bold text-green-600">
                  R$ {product.price.toFixed(2)}
                </span>
              </div>

              <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Package className="w-6 h-6 text-orange-600" />
                  <span className="text-lg font-medium">Disponível:</span>
                </div>
                <span className="text-2xl font-bold text-orange-600">
                  {product.quantity} unidades
                </span>
              </div>
            </div>

            {product.description && (
              <div>
                <h3 className="text-lg font-semibold mb-2">Descrição</h3>
                <p className="text-gray-700 leading-relaxed">{product.description}</p>
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="w-4 h-4" />
                <span>{product.location}, {product.producer_city}</span>
              </div>
              
              {/* Delivery Options Section */}
              {product.delivery_options && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    🚚 Opções de Entrega
                  </h4>
                  <div className="space-y-2">
                    {product.delivery_options.pickup && (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="border-green-300 text-green-700">
                          📍 Retirada no local
                        </Badge>
                        <span className="text-sm text-green-600">Disponível</span>
                      </div>
                    )}
                    {product.delivery_options.delivery && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="border-blue-300 text-blue-700">
                            🚚 Entrega
                          </Badge>
                          {product.delivery_options.delivery_fee && (
                            <span className="text-sm text-blue-600">
                              Taxa: R$ {product.delivery_options.delivery_fee.toFixed(2)}
                            </span>
                          )}
                        </div>
                        {product.delivery_options.delivery_radius && (
                          <p className="text-sm text-gray-600 ml-6">
                            Raio: {product.delivery_options.delivery_radius} km
                          </p>
                        )}
                        {product.delivery_options.delivery_notes && (
                          <p className="text-sm text-gray-600 ml-6 italic">
                            {product.delivery_options.delivery_notes}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Produtor</h4>
                <p className="text-gray-700">{product.producer_name}</p>
                <p className="text-sm text-gray-500">{product.producer_phone}</p>
              </div>

              {paymentMethods && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    💳 Formas de Pagamento Aceitas
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {paymentMethods.pix && (
                      <PaymentMethodBadge method="pix" />
                    )}
                    {paymentMethods.debit_card && (
                      <PaymentMethodBadge method="debit_card" />
                    )}
                    {paymentMethods.credit_card && paymentMethods.credit_card_brands.length > 0 ? (
                      paymentMethods.credit_card_brands.map(brand => (
                        <PaymentMethodBadge 
                          key={brand} 
                          method="credit_card" 
                          brand={brand}
                        />
                      ))
                    ) : paymentMethods.credit_card && (
                      <PaymentMethodBadge method="credit_card" />
                    )}
                    {!paymentMethods.pix && !paymentMethods.debit_card && !paymentMethods.credit_card && (
                      <Badge variant="outline" className="text-gray-600">
                        A combinar com o produtor
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>

            {siteSettings?.whatsapp_requires_login && !user ? (
              /* Show login required message when login is required and user not logged in */
              <div className="space-y-3">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  <div className="flex items-center justify-center mb-2">
                    <span className="text-2xl">🔒</span>
                  </div>
                  <h4 className="font-semibold text-yellow-800 mb-1">Login Necessário</h4>
                  <p className="text-sm text-yellow-700">
                    Para proteger a privacidade dos produtores, é necessário fazer login para contatá-los
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-3"
                >
                  <span className="mr-2">👤</span>
                  Fazer Login para Contatar Produtor
                </Button>
              </div>
            ) : (
              /* Show normal WhatsApp button when login not required or user is logged in */
              <Button
                onClick={handleWhatsAppClick}
                className="w-full whatsapp-btn text-lg py-3"
                data-testid="whatsapp-button"
              >
                <Phone className="w-5 h-5 mr-2" />
                Falar com Produtor via WhatsApp
              </Button>
            )}
          </div>
        </div>

        {/* Reviews Section */}
        <Card className="card-feira">
          <CardContent className="p-6">
            <ProductReviews product={product} onReviewAdded={handleReviewAdded} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductDetails;